import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { X, Rocket, Star, Sparkles, Moon, Sun, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const FEATURE_NAMES = {
  kundli: { en: 'Janam Kundli', hi: 'जन्म कुंडली' },
  chat: { en: 'AI Astrologer', hi: 'AI ज्योतिषी' },
  love_match: { en: 'Kundli Milan', hi: 'कुंडली मिलान' },
  palm_reading: { en: 'Palm Reading', hi: 'हस्तरेखा' },
  yearly_prediction: { en: 'Yearly Predictions', hi: 'वार्षिक भविष्यवाणी' },
  family_compatibility: { en: 'Family Compatibility', hi: 'पारिवारिक अनुकूलता' },
  profile: { en: 'Profile', hi: 'प्रोफाइल' },
  janam_patri: { en: 'Janam Patri', hi: 'जन्म पत्री' }
};

export const QuotaExhaustedPopup = ({ 
  isOpen, 
  onClose, 
  feature = 'feature'
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();

  const featureName = FEATURE_NAMES[feature]?.[language] || FEATURE_NAMES[feature]?.en || feature;

  const handleContinue = () => {
    onClose();
    navigate('/subscription');
  };

  const content = {
    hi: {
      title: 'आपकी ब्रह्मांडीय यात्रा यहीं नहीं रुकती!',
      subtitle: `आपने अपना मुफ्त ${featureName} उपयोग कर लिया है`,
      description: 'प्रीमियम में अपग्रेड करें और अपनी ज्योतिष यात्रा जारी रखें',
      benefits: [
        'असीमित कुंडली और भविष्यवाणियां',
        'विस्तृत वैदिक विश्लेषण',
        'व्यक्तिगत उपाय और मार्गदर्शन',
        'प्राथमिकता ज्योतिषी सहायता'
      ],
      continueBtn: 'यात्रा जारी रखें',
      skipBtn: 'बाद में',
      startingAt: 'केवल ₹99/वर्ष से शुरू'
    },
    en: {
      title: "Your cosmic journey doesn't stop here!",
      subtitle: `You've used your free ${featureName}`,
      description: 'Upgrade to premium and continue your astrology journey',
      benefits: [
        'Unlimited Kundli & Predictions',
        'Detailed Vedic Analysis',
        'Personalized Remedies & Guidance',
        'Priority Astrologer Support'
      ],
      continueBtn: 'Continue Journey',
      skipBtn: 'Maybe Later',
      startingAt: 'Starting at just ₹99/year'
    }
  };

  const t = content[language] || content.en;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 30 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-md rounded-3xl overflow-hidden ${
            isDark 
              ? 'bg-gradient-to-br from-white via-[#FFF9EF] to-[#FCE9D9]' 
              : 'bg-gradient-to-br from-white via-[#FFF9EF] to-[#FCE9D9]'
          }`}
        >
          {/* Cosmic background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Stars */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  opacity: Math.random() * 0.5 + 0.2
                }}
                animate={{
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  duration: Math.random() * 2 + 1,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }}
              />
            ))}
            {/* Gradient orbs */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-[#F5B942]/30 to-pink-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-[#C84B32]/30 to-orange-500/20 rounded-full blur-3xl" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${
              isDark ? 'hover:bg-[#FCE9D9] text-white/60' : 'hover:bg-[#FCE9D9]/60 text-[#6B6560]'
            }`}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="relative p-6 pt-8">
            {/* Animated Icon */}
            <div className="flex justify-center mb-5">
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C84B32] via-[#E85D43] to-[#F5B942] flex items-center justify-center shadow-lg shadow-[#C84B32]/30">
                  <Rocket className="w-12 h-12 text-white" />
                </div>
                {/* Orbiting elements */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                >
                  <Star className="absolute -top-2 -right-2 w-6 h-6 text-[#C84B32]" />
                </motion.div>
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                >
                  <Moon className="absolute -bottom-1 -left-2 w-5 h-5 text-[#C84B32]" />
                </motion.div>
              </motion.div>
            </div>

            {/* Title */}
            <h2 className={`text-xl sm:text-2xl font-bold text-center mb-2 font-['Fraunces'] ${
              isDark ? 'text-white' : 'text-[#282622]'
            }`}>
              {t.title}
            </h2>

            {/* Subtitle */}
            <p className={`text-center text-sm mb-1 ${
              isDark ? 'text-amber-600' : 'text-amber-600'
            }`}>
              {t.subtitle}
            </p>
            
            <p className={`text-center text-sm mb-5 ${
              isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'
            }`}>
              {t.description}
            </p>

            {/* Benefits */}
            <div className="space-y-2 mb-5">
              {t.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                  className={`flex items-center gap-3 p-2.5 rounded-xl ${
                    isDark ? 'bg-[#FCE9D9]/40' : 'bg-[#FCE9D9]/60'
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  <span className={`text-sm ${isDark ? 'text-[#282622]' : 'text-[#4A4640]'}`}>
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Price */}
            <p className={`text-center text-sm mb-4 ${
              isDark ? 'text-[#C84B32]' : 'text-[#C84B32]'
            } font-medium`}>
              <Sun className="w-4 h-4 inline mr-1" />
              {t.startingAt}
            </p>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleContinue}
                className="w-full py-4 bg-gradient-to-r from-[#C84B32] via-[#E85D43] to-[#F5B942] hover:from-[#C84B32] hover:via-[#E85D43] hover:to-[#F5B942] text-white font-bold text-base rounded-xl shadow-lg shadow-[#C84B32]/30 transition-all hover:scale-[1.02] group"
              >
                {t.continueBtn}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <button
                onClick={onClose}
                className={`w-full py-2 text-sm transition-colors ${
                  isDark ? 'text-[#6B6560] hover:text-[#4A4640]' : 'text-[#6B6560] hover:text-[#6B6560]'
                }`}
              >
                {t.skipBtn}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuotaExhaustedPopup;
